import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMutation, useQuery } from '@apollo/client';
import { Alert, Card, CardContent, Spacer, TextInput } from '@guildeducationinc/recess';
import DeleteOutlineIcon from '@guildeducationinc/recess/icons/DeleteOutlineIcon';
import EditIcon from '@guildeducationinc/recess/icons/EditIcon';
import VisibilityIcon from '@guildeducationinc/recess/icons/VisibilityIcon';
import { AdminTopNav } from '@src/components/Admin/AdminTopNav';
import { SAVE_GRADING_SCALE } from '@src/graphql/queries/gradingScale';
import { COUNTRIES } from '@src/graphql/queries/lookup';
import { useAuth } from '@src/hooks/useAuth';
import { convertToBaseGrade, useGradingScales } from '@src/hooks/useGradingScales';
import { GradeTypeSubset, GradingScaleType, } from '@src/types';
import { DataTable, Form, MuiMultiSelect, MuiSingleSelect, SearchFilterBar, TrackableButton, useAlert, useLocale, useSessionState, } from 'pe-core-frontend';
import { useMemo, useState } from 'react';
import styles from './GradingScale.module.scss';
export const createBaseGrade = (type) => {
    if (type === GradingScaleType.RANGE) {
        return { minValue: 0, maxValue: 100 };
    }
    return {
        items: [{ label: '', value: 1, gradeEquivalent: GradeTypeSubset.A }],
    };
};
export const GradingScale = () => {
    const { t } = useLocale();
    const { showAlert } = useAlert();
    const { isSuperuser } = useAuth();
    const [searchText, setSearchText] = useSessionState('gradingScaleSearch', '');
    const { gradingScales, loading, error, page, pageSize, setPage, setPageSize, total, orderBy, orderByDir, setOrderBy, setOrderByDir, refetch, } = useGradingScales({
        searchText,
        includeCountries: true,
    });
    const [saveGradingScale] = useMutation(SAVE_GRADING_SCALE);
    const [selectedGradingScale, setSelectedGradingScale] = useState(null);
    const [isViewOnly, setIsViewOnly] = useState(false);
    const handleSave = async (gs) => {
        try {
            const inputData = {
                id: gs.id || 0,
                isActive: gs.isActive,
                name: gs.name,
                gradingScaleType: gs.gradingScaleType,
                gradeLevels: Array.isArray(gs.gradeLevels) ? gs.gradeLevels[0] : gs.gradeLevels,
                extraData: gs.extraData,
                countryIds: gs.countries?.map(c => c.id),
            };
            const result = await saveGradingScale({
                variables: { input: inputData },
            });
            if (result.data?.saveGradingScale && !result.data.saveGradingScale.success) {
                return result.data.saveGradingScale;
            }
            await refetch();
            setSelectedGradingScale(null);
            const message = result.data.saveGradingScale.messages[0];
            const toShow = t(message.value, { values: message.params });
            showAlert(toShow, 'success');
        }
        catch (err) {
            throw new Error(t('Unable to save grading scale'));
        }
    };
    // Get Country Options
    const { data, loading: countriesLoading, error: countriesError, } = useQuery(COUNTRIES, {
        variables: { includeInactive: false },
    });
    const countryOptions = data?.countries.map((c) => ({
        id: c.params.id,
        code: c.value,
        name: c.label,
    }));
    // Build options from lookup; ensure selected countries appear even if not in lookup
    const { combinedCountryOptions, countryByIdMap } = useMemo(() => {
        const raw = (countryOptions || []).map((o) => ({
            value: o.id,
            label: o.name,
        }));
        const optionsMap = new Map();
        raw.forEach(o => optionsMap.set(o.value, o));
        const selected = selectedGradingScale?.countries || [];
        selected.forEach(c => {
            if (!optionsMap.has(c.id)) {
                optionsMap.set(c.id, { value: c.id, label: c.name });
            }
        });
        const byId = new Map();
        selected.forEach(c => byId.set(c.id, c));
        optionsMap.forEach((opt, id) => {
            if (!byId.has(id)) {
                byId.set(id, { id, name: opt.label, code: '' });
            }
        });
        return {
            combinedCountryOptions: Array.from(optionsMap.values()),
            countryByIdMap: byId,
        };
    }, [countryOptions, selectedGradingScale]);
    const isRangeGrade = (g) => 'minValue' in g && 'maxValue' in g;
    const isCategoryGrade = (g) => 'items' in g && g.items.length > 0;
    const handleValidate = (gs) => {
        const errors = {};
        if (!gs.name) {
            errors.name = t('Name is required');
        }
        if (!gs.gradingScaleType) {
            errors.gradingScaleType = t('Grading scale type is required');
        }
        if (!gs.gradeLevels ||
            (isCategoryGrade(gs.gradeLevels) && gs.gradeLevels.items.length === 0) ||
            (isRangeGrade(gs.gradeLevels) &&
                gs.gradeLevels.minValue === undefined &&
                gs.gradeLevels.maxValue === undefined)) {
            errors.gradeLevels = t('Grade Levels is required');
        }
        if (gs.gradingScaleType === GradingScaleType.RANGE) {
            if (!isRangeGrade(gs.gradeLevels)) {
                errors.gradeLevels = t('Grade Levels must be numeric');
            }
        }
        if (gs.gradingScaleType === GradingScaleType.CATEGORY) {
            if (!gs.gradeLevels.items.every(g => typeof g.label === 'string')) {
                errors.gradeLevels = t('Grade Levels labels must be strings');
            }
            if (!gs.gradeLevels.items.every(g => typeof g.value === 'number')) {
                errors.gradeLevels = t('Grade Levels values must be numbers');
            }
        }
        return errors;
    };
    // More are added in the form below
    const formFields = useMemo(() => {
        return [
            {
                name: 'name',
                label: t('Name'),
            },
            {
                name: 'isActive',
                label: t('Active?'),
                type: 'boolean',
                serializeValue: (value) => (value ? 'true' : 'false'),
                deserializeValue: (value) => value === 'true',
            },
        ];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const handleSortModelChange = (sortModel) => {
        if (sortModel && sortModel.length > 0) {
            const sort = sortModel[0];
            setOrderBy(sort.field);
            setOrderByDir(sort.sort);
        }
        else {
            setOrderBy(null);
            setOrderByDir(null);
        }
    };
    // Helper function to open grading scale modal with proper data transformation
    const openGradingScaleModal = (row, viewOnly) => {
        const type = row.gradingScaleType;
        setIsViewOnly(viewOnly);
        setSelectedGradingScale({
            ...row,
            gradeLevels: row.gradeLevels
                ? convertToBaseGrade(row.gradeLevels, type)
                : createBaseGrade(type),
        });
    };
    // Helper function to close modal and reset all states
    const closeModal = () => {
        setSelectedGradingScale(null);
        setIsViewOnly(false);
    };
    const handleAdd = () => {
        openGradingScaleModal({
            id: 0,
            gradingScaleType: GradingScaleType.RANGE,
            name: '',
            isActive: true,
            gradeLevels: { minValue: 0, maxValue: 100 },
            extraData: {},
            countries: [],
        }, false);
    };
    const handleOnEditClick = (row) => {
        openGradingScaleModal(row, false);
    };
    const handleOnViewClick = (row) => {
        openGradingScaleModal(row, true);
    };
    const gradeTypeLabelMap = {
        [GradeTypeSubset.A_PLUS]: 'A+',
        [GradeTypeSubset.A]: 'A',
        [GradeTypeSubset.A_MINUS]: 'A-',
        [GradeTypeSubset.B_PLUS]: 'B+',
        [GradeTypeSubset.B]: 'B',
        [GradeTypeSubset.B_MINUS]: 'B-',
        [GradeTypeSubset.C_PLUS]: 'C+',
        [GradeTypeSubset.C]: 'C',
        [GradeTypeSubset.C_MINUS]: 'C-',
        [GradeTypeSubset.D_PLUS]: 'D+',
        [GradeTypeSubset.D]: 'D',
        [GradeTypeSubset.D_MINUS]: 'D-',
        [GradeTypeSubset.F]: 'F',
    };
    return (_jsxs("div", { children: [_jsx(AdminTopNav, { title: t('Grading scales'), searchFilterActions: _jsx(SearchFilterBar, { enableSearch: true, searchText: searchText, onSearchChange: setSearchText, searchPlaceholder: t('Search by Name or Category') }), actions: isSuperuser ? (_jsx("div", { children: _jsx(TrackableButton, { btnType: 'mui', enText: 'Add Grading Scale', tag: 'header', color: 'primary', variant: 'contained', type: 'button', className: 'button button--primary margin--bottom_16', onClick: handleAdd, children: t('Add') }) })) : undefined }), _jsx("div", { children: _jsx(Card, { children: _jsxs(CardContent, { children: [error && (_jsx(Alert, { display: true, hideClose: true, type: 'danger', children: error.message })), _jsx(DataTable, { disableRowSelectionOnClick: true, disableColumnMenu: true, rows: gradingScales, loading: loading, pageSizeOptions: [10, 25], paginationMode: 'server', paginationModel: { page: page - 1, pageSize }, onPaginationModelChange: pagination => {
                                    setPage(pagination.page + 1);
                                    setPageSize(pagination.pageSize);
                                }, onSortModelChange: handleSortModelChange, sortModel: [{ field: orderBy ?? 'name', sort: orderByDir }], rowCount: total, columns: [
                                    {
                                        field: 'name',
                                        headerName: t('Name'),
                                        flex: 0.1,
                                    },
                                    {
                                        field: 'gradingScaleType',
                                        headerName: t('Type'),
                                        flex: 0.05,
                                        renderCell: params => t(params.value),
                                    },
                                    {
                                        field: 'gradeLevels',
                                        headerName: t('Grade Levels'),
                                        flex: 0.35,
                                        renderCell: params => isRangeGrade(params.row.gradeLevels)
                                            ? `min: ${params.row.gradeLevels.minValue}, max: ${params.row.gradeLevels.maxValue}`
                                            : params.row.gradeLevels.items
                                                .map(i => i.label)
                                                .join(', '),
                                    },
                                    {
                                        field: 'countries',
                                        headerName: t('Countries'),
                                        flex: 0.2,
                                        renderCell: params => {
                                            const countries = params.row.countries || [];
                                            const names = countries.map((c) => c.name);
                                            const shown = names.slice(0, 4).join(', ');
                                            const suffix = names.length > 4 ? ', ...' : '';
                                            return `${countries.length} | ${shown}${suffix}`;
                                        },
                                    },
                                    {
                                        field: 'isActive',
                                        headerName: t('Active?'),
                                        flex: 0.05,
                                        renderCell: params => (params.value ? t('Yes') : t('No')),
                                    },
                                    {
                                        field: 'actions',
                                        headerName: t('Actions'),
                                        flex: 0.05,
                                        renderCell: ({ row }) => (_jsx("div", { children: isSuperuser ? (_jsx(TrackableButton, { btnType: 'icon', enText: 'Edit', tag: `grading_scale_${row.id}`, appearance: 'minimal', label: t('Edit'), icon: _jsx(EditIcon, { size: 20 }), onClick: () => handleOnEditClick(row) })) : (_jsx(TrackableButton, { btnType: 'icon', enText: 'View', tag: `grading_scale_${row.id}`, appearance: 'minimal', label: t('View'), icon: _jsx(VisibilityIcon, { size: 20 }), onClick: () => handleOnViewClick(row) })) })),
                                    },
                                ] })] }) }) }), selectedGradingScale && (_jsx(Form, { fields: formFields, title: isViewOnly
                    ? t('View Grading Scale')
                    : selectedGradingScale?.id
                        ? t('Edit Grading Scale')
                        : t('Add Grading Scale'), modal: true, obj: selectedGradingScale, onValidate: isViewOnly ? undefined : handleValidate, onSave: isViewOnly ? undefined : handleSave, minHeight: 150, onCancel: closeModal, children: formObj => {
                    // The form maintains its own state. Use the form's setDraft to modify its internal state
                    // formObj.draft is the form's current state. we use it instead of selectedGradingScale
                    const updateGradeLevel = (idx, patch) => {
                        formObj.setDraft?.((draft) => {
                            const current = draft.gradeLevels;
                            if (isCategoryGrade(current)) {
                                const items = [...(current.items || [])];
                                items[idx] = { ...items[idx], ...patch };
                                return { ...draft, gradeLevels: { ...current, items } };
                            }
                            return {
                                ...draft,
                                gradeLevels: { ...current, ...patch },
                            };
                        });
                    };
                    const addGradeLevel = () => {
                        formObj.setDraft?.((draft) => {
                            // Only work with CATEGORY type
                            if (draft.gradingScaleType === GradingScaleType.CATEGORY) {
                                // Check if gradeLevels exists and has the right structure
                                let currentItems = [];
                                if (draft.gradeLevels && 'items' in draft.gradeLevels) {
                                    currentItems = draft.gradeLevels.items || [];
                                }
                                const newItem = {
                                    label: '',
                                    value: currentItems.length + 1,
                                    gradeEquivalent: GradeTypeSubset.A,
                                };
                                return {
                                    ...draft,
                                    gradeLevels: {
                                        items: [...currentItems, newItem],
                                    },
                                };
                            }
                            return draft;
                        });
                    };
                    const removeGradeLevel = (idxToRemove) => {
                        formObj.setDraft?.((draft) => {
                            if (draft.gradingScaleType === GradingScaleType.CATEGORY) {
                                if (draft.gradeLevels && 'items' in draft.gradeLevels) {
                                    const items = draft.gradeLevels.items || [];
                                    if (items.length > 1) {
                                        const next = items.filter((_, i) => i !== idxToRemove);
                                        return {
                                            ...draft,
                                            gradeLevels: {
                                                items: next,
                                            },
                                        };
                                    }
                                }
                            }
                            return draft;
                        });
                    };
                    const currentGradingScaleType = formObj.draft?.gradingScaleType || selectedGradingScale.gradingScaleType;
                    const currentGradeLevels = formObj.draft?.gradeLevels ||
                        selectedGradingScale.gradeLevels ||
                        createBaseGrade(currentGradingScaleType);
                    // Countries: merge fetched options with currently selected so defaults always render
                    const selectedCountries = formObj.draft?.countries ?? [];
                    return (_jsxs(_Fragment, { children: [_jsx("label", { className: styles.gradeLevelsTitle, children: t('Grading scale type') }), _jsx("select", { value: currentGradingScaleType || '', onChange: e => {
                                    const newType = e.target.value;
                                    if (formObj.setDraft) {
                                        formObj.setDraft((draft) => ({
                                            ...draft,
                                            gradingScaleType: newType,
                                            gradeLevels: createBaseGrade(newType),
                                        }));
                                    }
                                }, className: styles.passingRequirementDropdown, disabled: isViewOnly, children: Object.values(GradingScaleType).map(type => (_jsx("option", { value: type, children: t(type) }, type))) }), _jsxs("div", { className: styles.gradeLevelsSection, children: [_jsx("label", { className: styles.gradeLevelsTitle, children: t('Grade Levels') }), currentGradingScaleType === GradingScaleType.RANGE ? (_jsxs("table", { className: styles.gradeLevelsTable, children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { children: t('Min Value') }), _jsx("th", { children: t('Max Value') })] }) }), _jsx("tbody", { children: _jsxs("tr", { children: [_jsx("td", { children: _jsx(TextInput, { label: '', type: 'number', value: currentGradeLevels.minValue?.toString() || '', onChange: e => updateGradeLevel(0, { minValue: Number(e.target.value) }), disabled: isViewOnly }) }), _jsx("td", { children: _jsx(TextInput, { label: '', type: 'number', value: currentGradeLevels.maxValue?.toString() || '', onChange: e => updateGradeLevel(0, { maxValue: Number(e.target.value) }), disabled: isViewOnly }) })] }) })] })) : (_jsxs("table", { className: styles.gradeLevelsTable, children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { children: t('Label') }), _jsx("th", { children: t('Value (Rank)') }), _jsx("th", { className: styles.narrowCol, children: t('Grade Equivalence') }), !isViewOnly && _jsx("th", { className: styles.actionCol, children: t('Actions') })] }) }), _jsx("tbody", { children: (currentGradeLevels.items || []).map((grade, idx) => (_jsxs("tr", { children: [_jsx("td", { children: _jsx(TextInput, { label: '', value: grade.label || '', onChange: e => updateGradeLevel(idx, { label: e.target.value }), disabled: isViewOnly }) }), _jsx("td", { children: _jsx(TextInput, { label: '', type: 'number', value: grade.value || '', onChange: e => updateGradeLevel(idx, { value: Number(e.target.value) }), disabled: isViewOnly }) }), _jsx("td", { children: _jsx(MuiSingleSelect, { label: '', options: Object.values(GradeTypeSubset).map(type => ({
                                                                    label: gradeTypeLabelMap[type],
                                                                    value: type,
                                                                })), value: grade.gradeEquivalent?.toString() || '', onChange: e => updateGradeLevel(idx, {
                                                                    gradeEquivalent: e.target.value,
                                                                }), className: styles.noGap, disabled: isViewOnly }) }), !isViewOnly && (_jsx("td", { className: styles.actionCol, children: _jsx(TrackableButton, { btnType: 'icon', enText: 'Delete grade level', tag: `delete_grade_${idx}`, appearance: 'minimal', label: t('Delete'), icon: _jsx(DeleteOutlineIcon, { size: 20 }), onClick: () => removeGradeLevel(idx) }) }))] }, grade.value?.toString() || `grade-level-${idx}`))) })] }))] }), currentGradingScaleType === GradingScaleType.CATEGORY && !isViewOnly && (_jsx("div", { className: styles.gradeLevelActions, children: _jsx("button", { type: 'button', className: styles.addGradeButton, onClick: addGradeLevel, children: t('Add Grade Level') }) })), formObj.errors?.gradeLevels && (_jsx("div", { className: styles.formError, children: formObj.errors.gradeLevels })), _jsx(Spacer, { margin: 8 }), combinedCountryOptions.length > 0 && !countriesError ? (_jsx(MuiMultiSelect, { label: t('Countries'), options: combinedCountryOptions, value: selectedCountries?.map(c => c.id) || [], onChange: event => {
                                    const selectedIds = event.selection?.map(x => x.value) ?? [];
                                    formObj.setDraft?.((draft) => ({
                                        ...draft,
                                        countries: selectedIds
                                            .map(id => countryByIdMap.get(id))
                                            .filter((c) => Boolean(c)),
                                    }));
                                }, className: styles.countrySelect, disabled: countriesLoading || isViewOnly })) : (_jsx("div", { children: t('There are no active countries to select from. Please Add a Country.') }))] }));
                } }, selectedGradingScale.id))] }));
};
